"use client";

import { type FC, useCallback, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { FeedbackTranslations } from "@jti/fe-common/config";
import type { VideoPlayerProps as VideoPlayerUIProps } from "@jti/ui/components/VideoPlayer";
import { VideoPlayer as VideoPlayerUI } from "@jti/ui/components/VideoPlayer";

import { usePublisherData } from "./_internal/usePublisherData";
import { getDurationInSeconds } from "./utils/getDurationInSeconds";

import { VideoFeedback } from "./VideoFeedback";
import { VideoObjectJsonLD } from "./VideoObjectJsonLD";
import type { videoPlayerInputs } from "./VideoPlayer.builder";

export type VideoPlayerProps = BuilderComponentProps<typeof videoPlayerInputs>;

export const VideoPlayer: FC<VideoPlayerProps> = (props) => {
  const publisher = usePublisherData();
  const { isFeedbackMechanismEnabled, feedbackTranslations, feedbackSignUpUrl = "" } = useConfig();
  const [showFeedback, setShowFeedback] = useState(false);

  const isVideoFeedbackEnabled = Boolean(isFeedbackMechanismEnabled && props.feedbackEnabled);

  const handleVideoEnded = useCallback(() => {
    if (isVideoFeedbackEnabled) {
      setShowFeedback(true);
    }
  }, [isVideoFeedbackEnabled]);

  const handleFeedbackClose = useCallback(() => {
    setShowFeedback(false);
  }, []);

  const feedbackOverlay = showFeedback ? (
    <VideoFeedback
      videoUrl={getVideoUrl(props)}
      feedbackTranslations={feedbackTranslations as FeedbackTranslations}
      authRegisterPageUrl={feedbackSignUpUrl}
      handleClose={handleFeedbackClose}
    />
  ) : null;

  const playerControlsProps: Partial<VideoPlayerUIProps> = {
    showFullscreen: showFeedback ? false : props.showFullscreen,
    showControls: showFeedback ? false : props.showControls,
    showControlsWhenPlaying: showFeedback ? false : props.showControlsWhenPlaying,
    isMainPlayButtonAllowed: !showFeedback,
  };

  const commonProps: Partial<VideoPlayerUIProps> = {
    ...playerControlsProps,
    poster: props.posterImage,
    description: props.description,
    playLabel: props.playLabel,
    replayLabel: props.replayLabel,
    seekLabel: props.seekLabel,
    soundOnLabel: props.soundOnLabel,
    muteLabel: props.muteLabel,
    volumeLabel: props.volumeLabel,
    showFullscreenLabel: props.showFullScreenLabel,
    showDuration: props.duration,
    fallbackDuration: getDurationInSeconds(props.jsonLdDuration),
    disableSeek: props.disableSeek,
    preventMainPlayButtonClick: props.preventMainPlayButtonClick,
    preload: props.preload,
    contentSize: props.contentSize,
    themeClass: props.controlColor === "light" ? "dark-theme" : "light-theme",
    title: props.title,
  };

  if (props.videoType === "youtube") {
    const uiProps: VideoPlayerUIProps = {
      ...commonProps,
      desktopSrc: "",
      externalLinkVideo: props?.youtubeConfiguration?.externalLinkVideo,
      hostedExternally: true,
      onEnded: handleVideoEnded,
    };

    return (
      <>
        <VideoObjectJsonLD
          name={props.title}
          description={props.description}
          thumbnailUrl={props.posterImage}
          thumbnailAlt={props.posterAlt}
          contentUrl={props.youtubeConfiguration?.externalLinkVideo}
          uploadDate={props.jsonLdUploadDate}
          duration={props.jsonLdDuration}
          ineligibleRegion={props.jsonLdIneligibleRegion}
          publisher={publisher}
        />
        <VideoPlayerUI {...uiProps}>{feedbackOverlay}</VideoPlayerUI>
      </>
    );
  }

  const uiProps: VideoPlayerUIProps = {
    ...commonProps,
    loop: props.hostedVideoConfiguration?.loop,
    autoPlay: props.hostedVideoConfiguration?.autoPlay,
    desktopSrc: props.hostedVideoConfiguration?.desktopSrc || "",
    tabletSrc: props.hostedVideoConfiguration?.tabletSrc,
    mobileSrc: props.hostedVideoConfiguration?.mobileSrc,
    alt: props.posterAlt,
    onEnded: handleVideoEnded,
  };
  const contentSrc =
    props.hostedVideoConfiguration?.desktopSrc ||
    props.hostedVideoConfiguration?.tabletSrc ||
    props.hostedVideoConfiguration?.mobileSrc ||
    "";

  return (
    <>
      <VideoObjectJsonLD
        name={props.title}
        description={props.description}
        thumbnailUrl={props.posterImage}
        thumbnailAlt={props.posterAlt}
        contentUrl={contentSrc}
        uploadDate={props.jsonLdUploadDate}
        duration={props.jsonLdDuration}
        ineligibleRegion={props.jsonLdIneligibleRegion}
        publisher={publisher}
      />
      <VideoPlayerUI {...uiProps}>{feedbackOverlay}</VideoPlayerUI>
    </>
  );
};

const getVideoUrl = (props: VideoPlayerProps): string => {
  if (props.videoType === "youtube") {
    return props.youtubeConfiguration?.externalLinkVideo || "";
  }
  return (
    props.hostedVideoConfiguration?.desktopSrc ||
    props.hostedVideoConfiguration?.tabletSrc ||
    props.hostedVideoConfiguration?.mobileSrc ||
    ""
  );
};
